import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import './SupportCenterPage.css';

interface Ticket {
  id: string;
  subject: string;
  userName: string;
  updatedAt: any;
  createdAt?: any;
  lastCustomerMessageAt?: any;
  lastReplyRole?: string;
  status?: string;
  category?: string;
  assignedTo?: string;
  assignedToName?: string;
  needsReply?: boolean;
  statusChangedBy?: string;
}

const ACTIVE_STATUSES = ['open', 'in_progress', 'waiting_on_user'];

// One "Active" tab mixed the two halves of the queue: the cases we still owe an
// answer and the ones we have already answered. Support had to read every row's
// badge to find their own work, and most of the queue was somebody else's turn
// (measured 2026-09-22: 21 active tickets, 4 of them waiting on us).
//
// Open and Waiting on customer split them on the same rule the badge and the
// waiting clock already use (isWaitingOnUs), so Open IS the to-do list.
//
// Open and Waiting on customer run the same active-status query and differ
// only in how the snapshot is filtered afterwards.
//
// There used to be a Mine tab. The owner picker replaced it (COE-221): support
// asked to see anybody's cases, not only their own, and "Me" in the picker
// does what Mine did while also splitting into Open and Waiting.
const STATUS_TABS: { key: string; label: string; statuses: string[] }[] = [
  { key: 'open',     label: 'Open',                 statuses: ACTIVE_STATUSES },
  { key: 'waiting',  label: 'Waiting on customer',  statuses: ACTIVE_STATUSES },
  { key: 'resolved', label: 'Resolved',             statuses: ['resolved'] },
  { key: 'closed',   label: 'Closed',               statuses: ['closed'] },
  { key: 'all',      label: 'All',                  statuses: [] }, // [] = no status filter
];

// Machine values (document_verification, waiting_on_user) are single tokens
// with no wrap opportunity in them, so a narrow column could only either widen
// or spill over its neighbour. Spaces give the browser somewhere to break, and
// read better besides. The detail page already shows the category this way.
const humanize = (value?: string) => (value ? value.replace(/_/g, ' ') : '');

const HOUR_MS = 3600000;

// Support's thresholds, and the same promise the acknowledgement email makes
// ("within 1 business day"): amber once a customer has waited a day, red at two.
const AMBER_AFTER_MS = 24 * HOUR_MS;
const RED_AFTER_MS = 48 * HOUR_MS;

const toMs = (timestamp: any): number => {
  if (!timestamp) return 0;
  if (timestamp.seconds) return timestamp.seconds * 1000;
  if (timestamp._seconds) return timestamp._seconds * 1000;
  const time = new Date(timestamp).getTime();
  return isNaN(time) ? 0 : time;
};

// When the customer started waiting for this reply.
//
// NOT updatedAt: an agent changing the status or the assignee bumps it, so a
// ticket nobody had answered for two days could read as touched a minute ago.
// Tickets that predate lastCustomerMessageAt have no honest answer, and get no
// colour rather than a made-up one (scripts/backfill_last_customer_message_at.py
// fills them in).
const waitingSinceMs = (ticket: Ticket): number => toMs(ticket.lastCustomerMessageAt);

// Whether WE owe the next message. Only these get a colour: red has to mean
// "we are late answering somebody", or it stops meaning anything.
//
// A ticket parked on waiting_on_user is waiting on them, not us. And when
// triage has explicitly cleared the last customer message (needsReply false —
// a thank-you, say), we do not owe a reply even though they spoke last;
// without that check a row showed an "Answered" badge and a red clock at once.
//
// An agent who picks Open or In progress by hand after answering is saying the
// ball is back with us; the tab has to follow the status they chose, or the
// page reads "Open" while the inbox files it under Waiting (COE-221).
const isWaitingOnUs = (ticket: Ticket): boolean => {
  if (ticket.status === 'waiting_on_user') return false;
  if (ticket.status === 'resolved' || ticket.status === 'closed') return false;
  if (ticket.statusChangedBy === 'agent') return true;
  if (ticket.needsReply === false) return false;
  if (typeof ticket.lastReplyRole === 'string') return ticket.lastReplyRole !== 'support';
  // Legacy tickets carry no lastReplyRole; fall back to the triage flag.
  return Boolean(ticket.needsReply);
};

const formatElapsed = (fromMs: number, nowMs: number): string => {
  const totalMinutes = Math.max(0, Math.floor((nowMs - fromMs) / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const waitSeverityClass = (elapsedMs: number): string => {
  if (elapsedMs >= RED_AFTER_MS) return 'case-open-elapsed--stale';
  if (elapsedMs >= AMBER_AFTER_MS) return 'case-open-elapsed--aging';
  return 'case-open-elapsed--fresh';
};

function renderWaiting(ticket: Ticket, nowMs: number) {
  const since = waitingSinceMs(ticket);
  if (!since) {
    // Honest blank beats a badge invented from updatedAt.
    return (
      <span
        className="support-waiting-idle"
        data-cy="inbox-waiting"
        title="This ticket predates the waiting clock, so we do not know when the customer last wrote."
      >
        unknown
      </span>
    );
  }

  const elapsed = Math.max(0, nowMs - since);
  const age = formatElapsed(since, nowMs);
  const since_label = new Date(since).toLocaleString();

  // Every ticket shows its age. Blanking the ones we had answered hid the
  // entire stale backlog — 13 of 19 active tickets — behind a dash, and made
  // the column look broken. Only the colour is reserved for what we owe.
  if (!isWaitingOnUs(ticket)) {
    return (
      <span
        className="support-waiting-idle"
        data-cy="inbox-waiting"
        title={
          ticket.status === 'waiting_on_user'
            ? `Waiting on the customer. They last wrote ${since_label}.`
            : `We answered last. The customer last wrote ${since_label}.`
        }
      >
        {age}
        <span className="support-waiting-note">
          {ticket.status === 'waiting_on_user' ? 'on customer' : 'answered'}
        </span>
      </span>
    );
  }

  return (
    <span
      className={`case-open-elapsed ${waitSeverityClass(elapsed)}`}
      data-cy="inbox-waiting"
      title={`Customer waiting for a reply since ${since_label}`}
    >
      {age}
    </span>
  );
}

// Owner picker values. Anything else is an agent's email.
const OWNER_ANYONE = '';
const OWNER_ME = '__me__';
const OWNER_UNASSIGNED = '__unassigned__';

const OWNER_STORAGE_KEY = 'supportInbox.owner';
const SORT_STORAGE_KEY = 'supportInbox.sort';

const matchesOwner = (ticket: Ticket, owner: string, myEmail: string): boolean => {
  if (owner === OWNER_ANYONE) return true;
  const assigned = (ticket.assignedTo ?? '').toLowerCase();
  if (owner === OWNER_UNASSIGNED) return !assigned;
  // Both sides default to '', so without the Boolean an agent whose account
  // carries no email would match every UNASSIGNED ticket under "Me" and read
  // the queue as entirely theirs.
  const target = owner === OWNER_ME ? myEmail : owner.toLowerCase();
  return Boolean(target) && assigned === target;
};

type SortKey = 'subject' | 'category' | 'user' | 'status' | 'waiting' | 'updated';
type SortDir = 'asc' | 'desc';
interface SortState {
  key: SortKey;
  dir: SortDir;
}

// Newest activity first, the order the query has always returned.
const DEFAULT_SORT: SortState = { key: 'updated', dir: 'desc' };

// firstDir is what one click gives: A to Z for text, the longest wait first
// (oldest "since" timestamp) for Waiting, the newest first for Last Updated.
const COLUMNS: { key: SortKey; label: string; firstDir: SortDir }[] = [
  { key: 'subject',  label: 'Subject',      firstDir: 'asc' },
  { key: 'category', label: 'Category',     firstDir: 'asc' },
  { key: 'user',     label: 'User',         firstDir: 'asc' },
  { key: 'status',   label: 'Status',       firstDir: 'asc' },
  { key: 'waiting',  label: 'Waiting',      firstDir: 'asc' },
  { key: 'updated',  label: 'Last Updated', firstDir: 'desc' },
];

// Lifecycle order, not alphabetical: "in progress" before "open" helps nobody.
const STATUS_ORDER = ['open', 'in_progress', 'waiting_on_user', 'resolved', 'closed'];

// null = no value. Those rows go last whichever way the column is sorted, so
// a ticket that predates the waiting clock never floats to the top as "the
// longest wait".
const sortValue = (ticket: Ticket, key: SortKey): string | number | null => {
  switch (key) {
    case 'subject':
      return (ticket.subject ?? '').toLowerCase() || null;
    case 'category':
      return humanize(ticket.category).toLowerCase() || null;
    case 'user':
      return (ticket.userName ?? '').toLowerCase() || null;
    case 'status': {
      const index = STATUS_ORDER.indexOf(ticket.status ?? '');
      return index === -1 ? STATUS_ORDER.length : index;
    }
    case 'waiting':
      return waitingSinceMs(ticket) || null;
    default:
      return toMs(ticket.updatedAt) || null;
  }
};

const compareTickets = (a: Ticket, b: Ticket, sort: SortState): number => {
  const av = sortValue(a, sort.key);
  const bv = sortValue(b, sort.key);
  if (av === null || bv === null) {
    if (av !== bv) return av === null ? 1 : -1;
  } else {
    const diff =
      typeof av === 'string' && typeof bv === 'string'
        ? av.localeCompare(bv)
        : (av as number) - (bv as number);
    if (diff !== 0) return sort.dir === 'asc' ? diff : -diff;
  }
  // Ties keep the default order, so equal rows do not shuffle on every update.
  return toMs(b.updatedAt) - toMs(a.updatedAt);
};

// The picker and the sort survive a reload. Storage can be blocked (private
// window, cleared site data), and then the page just starts from the defaults.
const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not remembering a preference is fine.
  }
};

const readStoredSort = (): SortState => {
  try {
    const parsed = JSON.parse(readStored(SORT_STORAGE_KEY) ?? '');
    if (
      COLUMNS.some((column) => column.key === parsed?.key) &&
      (parsed.dir === 'asc' || parsed.dir === 'desc')
    ) {
      return { key: parsed.key, dir: parsed.dir };
    }
  } catch {
    // Fall through to the default.
  }
  return DEFAULT_SORT;
};

interface SupportUser {
  email: string;
  name: string;
}

const SupportCenterPage: React.FC = () => {
  // Whatever the current tab's query returned; the tab split, the owner and
  // the sort are applied on top in render.
  const [snapshotTickets, setSnapshotTickets] = useState<Ticket[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('open');
  // The whole active queue, so an agent sitting on another tab can still see
  // how much is waiting on us. Refreshed by every snapshot that carries it
  // (every tab but Resolved and Closed); on those two the last known queue
  // stays on screen.
  const [activeQueue, setActiveQueue] = useState<Ticket[] | null>(null);
  const [owner, setOwner] = useState<string>(() => readStored(OWNER_STORAGE_KEY) ?? OWNER_ANYONE);
  const [sort, setSort] = useState<SortState>(readStoredSort);
  const [supportUsers, setSupportUsers] = useState<SupportUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Re-render once a minute so a ticket crossing 24h turns amber on a screen
  // that has been open all morning, which is exactly when nobody reloads.
  const [now, setNow] = useState(() => Date.now());

  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8090';

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Every agent, so the picker can offer someone who has nothing assigned
  // right now. A failure is quiet: the picker still lists every owner it can
  // see on the loaded tickets.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${BACKEND_URL}/support-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled && Array.isArray(data?.users)) setSupportUsers(data.users);
      } catch {
        // Covered by the owners on the tickets.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, BACKEND_URL]);

  const handleTabClick = (key: string) => {
    setActiveTab(key);
  };

  const handleOwnerChange = (value: string) => {
    setOwner(value);
    writeStored(OWNER_STORAGE_KEY, value);
  };

  const handleSort = (key: SortKey) => {
    const column = COLUMNS.find((c) => c.key === key)!;
    const next: SortState =
      sort.key === key
        ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: column.firstDir };
    setSort(next);
    writeStored(SORT_STORAGE_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const statuses =
      STATUS_TABS.find((tab) => tab.key === activeTab)?.statuses ?? [];
    const ticketsQuery =
      statuses.length === 0
        ? query(collection(db, "support_tickets"), orderBy("updatedAt", "desc"))
        : query(
            collection(db, "support_tickets"),
            where("status", "in", statuses),
            orderBy("updatedAt", "desc")
          );

    const unsubscribe = onSnapshot(ticketsQuery, (snapshot) => {
      // id last: a document field named id would otherwise shadow the real
      // document id, and two rows could end up sharing a React key.
      const ticketsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Ticket));

      // Resolved and Closed are the only queries that cannot see the whole
      // active queue, so they are the only ones that must not touch the counts.
      if (activeTab !== 'resolved' && activeTab !== 'closed') {
        setActiveQueue(
          ticketsData.filter((ticket) => ACTIVE_STATUSES.includes(ticket.status ?? ''))
        );
      }

      setSnapshotTickets(ticketsData);
      setIsLoading(false);
    }, (err) => {
      console.error("Error fetching real-time tickets:", err);
      setError("Failed to load tickets.");
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user, activeTab]);

  const myEmail = (user?.email ?? '').toLowerCase();

  const tickets = useMemo(() => {
    const statuses = STATUS_TABS.find((tab) => tab.key === activeTab)?.statuses ?? [];
    return snapshotTickets
      .filter((ticket) => {
        // Until the new tab's snapshot lands, the previous tab's rows are
        // still in state; never show them under the wrong tab.
        if (statuses.length > 0 && !statuses.includes(ticket.status ?? '')) return false;
        // Open is what support owes an answer; Waiting on customer is
        // everything active we have already handled. Same predicate, so a
        // ticket is in exactly one of them and never in neither.
        if (activeTab === 'open') return isWaitingOnUs(ticket);
        if (activeTab === 'waiting') return !isWaitingOnUs(ticket);
        return true;
      })
      .filter((ticket) => matchesOwner(ticket, owner, myEmail))
      .sort((a, b) => compareTickets(a, b, sort));
  }, [snapshotTickets, activeTab, owner, myEmail, sort]);

  // The tab counts follow the owner picker: with "Me" picked they are my
  // open and my waiting cases, which is what the Mine tab used to answer.
  const activeCounts = useMemo(() => {
    if (!activeQueue) return null;
    const scoped = activeQueue.filter((ticket) => matchesOwner(ticket, owner, myEmail));
    const owed = scoped.filter(isWaitingOnUs).length;
    return { open: owed, waiting: scoped.length - owed };
  }, [activeQueue, owner, myEmail]);

  // Agents from the backend plus anyone assigned on a loaded ticket (covers a
  // failed /support-users call), plus the stored pick if it is neither, so the
  // select never silently shows a value it has no option for.
  const ownerOptions = useMemo(() => {
    const byEmail = new Map<string, string>();
    supportUsers.forEach((candidate) => {
      if (candidate.email) byEmail.set(candidate.email.toLowerCase(), candidate.name || candidate.email);
    });
    [...(activeQueue ?? []), ...snapshotTickets].forEach((ticket) => {
      const email = (ticket.assignedTo ?? '').toLowerCase();
      if (email && !byEmail.has(email)) byEmail.set(email, ticket.assignedToName || ticket.assignedTo!);
    });
    if (
      owner !== OWNER_ANYONE &&
      owner !== OWNER_ME &&
      owner !== OWNER_UNASSIGNED &&
      !byEmail.has(owner.toLowerCase())
    ) {
      byEmail.set(owner.toLowerCase(), owner);
    }
    // "Me" already covers the signed-in agent.
    if (myEmail) byEmail.delete(myEmail);
    return Array.from(byEmail, ([email, name]) => ({ email, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [supportUsers, activeQueue, snapshotTickets, owner, myEmail]);

  const activeTabLabel =
    STATUS_TABS.find((tab) => tab.key === activeTab)?.label.toLowerCase() ?? '';

  const emptyMessage = (() => {
    if (owner === OWNER_ME) {
      return activeTab === 'open'
        ? 'Nothing assigned to you is waiting on us.'
        : `No ${activeTabLabel} support tickets are assigned to you.`;
    }
    if (owner === OWNER_UNASSIGNED) return `No unassigned ${activeTabLabel} support tickets.`;
    if (owner !== OWNER_ANYONE) {
      const name = ownerOptions.find((o) => o.email === owner.toLowerCase())?.name ?? owner;
      return `No ${activeTabLabel} support tickets are assigned to ${name}.`;
    }
    if (activeTab === 'open') return 'Nothing is waiting on us. Every active case has been answered.';
    if (activeTab === 'waiting') return 'No active cases are waiting on a customer.';
    return `No ${activeTabLabel} support tickets.`;
  })();

  return (
    <div className="task-view" data-cy="support-inbox">
      <div className="task-view-header">
        <h2>Support Center</h2>
      </div>
      <div className="support-inbox-toolbar">
        <div className="tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => handleTabClick(tab.key)}
              data-cy={`inbox-tab-${tab.key}`}
            >
              {tab.label}
              {activeCounts && (tab.key === 'open' || tab.key === 'waiting') ? (
                <span
                  className={
                    tab.key === 'open' && activeCounts.open > 0
                      ? 'tab-count tab-count--urgent'
                      : 'tab-count'
                  }
                  data-cy={`inbox-tab-count-${tab.key}`}
                >
                  {tab.key === 'open' ? activeCounts.open : activeCounts.waiting}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <label className="support-owner-filter">
          <span>Owner</span>
          <select
            value={owner}
            onChange={(e) => handleOwnerChange(e.target.value)}
            data-cy="inbox-owner-filter"
          >
            <option value={OWNER_ANYONE}>Anyone</option>
            <option value={OWNER_ME}>Me</option>
            <option value={OWNER_UNASSIGNED}>Unassigned</option>
            {ownerOptions.map((option) => (
              <option key={option.email} value={option.email}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="case-list-header support-inbox-row" role="row">
        {COLUMNS.map((column) => {
          const sorted = sort.key === column.key;
          return (
            <div
              key={column.key}
              role="columnheader"
              aria-sort={sorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <button
                type="button"
                className={sorted ? 'support-sort support-sort--active' : 'support-sort'}
                onClick={() => handleSort(column.key)}
                data-cy={`inbox-sort-${column.key}`}
                title={`Sort by ${column.label.toLowerCase()}`}
              >
                {column.label}
                <span className="support-sort-arrow" aria-hidden="true">
                  {sorted ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="case-list">
        {isLoading ? (
          <p>Loading tickets...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : tickets.length > 0 ? (
          tickets.map(ticket => (
            <div key={ticket.id} className="case-list-item support-inbox-row" data-cy="inbox-row">
              <div>
                {/* Driven by isWaitingOnUs, not by needsReply alone: the tabs
                    split on isWaitingOnUs, and a legacy ticket triage never
                    ran on (no needsReply, customer spoke last) used to land in
                    the to-do queue wearing an "Answered" badge. */}
                {isWaitingOnUs(ticket) ? (
                  <span className="todo-badge" data-cy="inbox-todo" title="The latest customer message is waiting for a reply">
                    ● To-do
                  </span>
                ) : (
                  <span className="todo-badge todo-badge-clear" data-cy="inbox-todo" title="No reply needed right now">
                    Answered
                  </span>
                )}
                <strong><Link to={`/support/ticket/${ticket.id}`} className="service-link">{ticket.subject}</Link></strong>
                {ticket.assignedTo ? (
                  <span
                    className="assignment-badge assignment-badge-assigned"
                    data-cy="inbox-assignee"
                  >
                    {ticket.assignedToName || ticket.assignedTo}
                  </span>
                ) : (
                  <span
                    className="assignment-badge assignment-badge-unassigned"
                    data-cy="inbox-assignee"
                  >
                    Unassigned
                  </span>
                )}
              </div>
              <div>{humanize(ticket.category) || '—'}</div>
              <div>{ticket.userName}</div>
              <div>{humanize(ticket.status) || '—'}</div>
              <div className="support-waiting-cell">{renderWaiting(ticket, now)}</div>
              <div>
                {toMs(ticket.updatedAt)
                  ? new Date(toMs(ticket.updatedAt)).toLocaleString()
                  : '—'}
              </div>
            </div>
          ))
        ) : (
          <p data-cy="inbox-empty">{emptyMessage}</p>
        )}
      </div>
    </div>
  );
};

export default SupportCenterPage;
